import { getCssFontStyle } from 'src/lib/design-system/typography';
import styled from 'threads5/themes/styled';

export const Strip = styled('div')({
  height: '3px',
  background:
    'linear-gradient(90deg, #78BEFC 0%, #DAACF7 52.15%, #98AFFB 100%)',
  width: '100%',
});

export const AccordionDetails = styled('details')(({ theme }) => {
  return {
    interpolateSize: 'allow-keywords',
    borderBottom: `1px solid ${theme.palette['web3/component/faq/divider']}`,
    paddingBottom: '10px',
    transition: 'padding-bottom 0.3s ease',
    '&::details-content': {
      transition: 'block-size 0.3s, content-visibility 0.3s allow-discrete',
      overflow: 'hidden',
      blockSize: '0',
      marginTop: '-10px',
    },
    '&[open]': {
      boxShadow: theme.palette['web3/component/accordion/box-shadow'],
      backdropFilter: 'blur(2px)',
      background: theme.palette['web3/component/faq/background'],
      borderRadius: '12px 12px 0 0',
      paddingBottom: 0,
      '.accordion-icon-chevron': {
        color: theme.palette['web3/component/faq/icon/color-expanded'],
        // points up when expanded; the `rotate` property (not `transform`)
        // interpolates as a signed angle, so 180deg goes clockwise
        rotate: '180deg',
      },
    },
    '&[open]::details-content': {
      blockSize: 'auto',
    },
    // Hide border on element that comes before an open one
    '&:has(+ &[open])': {
      borderBottom: '1px solid transparent',
    },
    '.accordion-icon-chevron': {
      color: theme.palette['web3/component/faq/icon/color'],
      rotate: '0deg',
      transition: 'rotate 0.3s ease, color 0.3s ease',
    },
  };
});

export const AccordionDetailsSummary = styled('summary')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    cursor: 'pointer',
    padding: '26px 20px 26px 20px',
    position: 'relative',
    transition: 'opacity 0.3s ease',
    h3: {
      color: theme.palette['web3/component/faq/text/question'],
      margin: 0,
      ...getCssFontStyle('P1 SemiBold', 'xs'),
      ...getCssFontStyle('P1 SemiBold', 'md'),
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.palette['web3/component/faq/background/hover'],
      opacity: 0,
      transition: 'opacity 0.3s ease',
      pointerEvents: 'none',
    },
    '&:hover': {
      '&::before': {
        opacity: 0.3,
      },
    },
    // Disable hover background when details is open
    'details[open] &:hover::before': {
      opacity: 0,
    },
  };
});

export const AccordionDetailsContent = styled('div')(({ theme }) => {
  const baseStyles = {
    color: theme.palette['web3/component/faq/text/answer'],
    margin: 0,
  };

  return {
    display: 'flex',
    padding: '0 20px 40px 20px',
    flexDirection: 'column',
    [theme.breakpoints.down('md')]: {
      '& > *:not(:first-child)': {
        marginTop: '2.4rem',
      },
    },
    '& p': baseStyles,
    '& ul': {
      margin: '8px 0 0 24px',
      listStyle: 'disc',
      color: theme.palette['web3/component/faq/text/answer'],
      '& li p': {
        margin: '0',
        padding: '0',
      },
    },
    '& ol': {
      margin: '8px 0 0 24px',
      listStyle: 'auto',
      color: theme.palette['web3/component/faq/text/answer'],
      '& li p': {
        margin: '0',
        padding: '0',
      },
    },
    '& .btn': {
      marginTop: '24px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.8rem',
      '& p': {
        fontSize: '1.8rem',
      },
    },
    '& p:nth-last-of-type(1):empty': {
      display: 'none',
    },
  };
});

export const AccordionIcons = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    width: '20px',
    height: '20px',
    '.accordion-icon-chevron': {
      position: 'absolute',
      top: 0,
      right: 0,
      transition:
        'opacity 0.3s ease-in-out, rotate 0.3s ease-in-out, color 0.3s ease-in-out',
    },
  };
});
